// Client tipado de `loan-credit-business`: una función por operationId del OpenAPI, bloque público
// del ciclo de crédito y bloque backoffice MDM. Los tipos por crédito salen de `@fiado/type-kit`.
import type { LoanBorrowerResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanBorrowerResponse';
import type { LoanContractResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanContractResponse';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import type { LoanDeviceLockResult } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanDeviceLockResult';
import type { LoanDeviceNotifyResult } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanDeviceNotifyResult';
import type { LoanDeviceReleaseResult } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanDeviceReleaseResult';
import type { LoanDevicePinUnlockResult } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanDevicePinUnlockResult';
import type { LoanDeviceUnlockResult } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanDeviceUnlockResult';
import type { BackofficeSendRegulatoryNoticeRequest } from '@fiado/type-kit/bin/loanCredit/dtos/requests/BackofficeSendRegulatoryNoticeRequest';
import type { RegulatoryNoticeCampaignPage } from '@fiado/type-kit/bin/loanCredit/dtos/responses/RegulatoryNoticeCampaignPage';
import type { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { apiFetch } from '../../client';
import type {
    BackofficeApplyPrepaymentRequest,
    CollectionQuotaResponse,
    CreateSellerPaymentRequest,
    CustomerManualMessageRequest,
    CustomerManualMessageResponse,
    CreditCustomersPage,
    CreditCustomersQuery,
    InStorePaymentListQuery,
    InStorePaymentListResponse,
    LoanBorrowerCreditItem,
    LoanContractDownloadUrl,
    LoanCreditDetail,
    LoanCreditNotificationListQuery,
    LoanCreditNotificationListResponse,
    LoanCreditPortfolioQuery,
    LoanCreditPortfolioResponse,
    LoanDeviceEnrollResult,
    LoanDeviceExtendValidityRequest,
    LoanDeviceExtendValidityResult,
    LoanDeviceFleetItem,
    LoanDeviceFleetQuery,
    LoanDeviceFleetResponse,
    LoanDeviceLockRequest,
    LoanDeviceNotifyRequest,
    LoanDeviceOperationsPage,
    LoanDeviceRefreshResult,
    LoanDeviceReleaseRequest,
    LoanDeviceUnlockRequest,
    LoanAdHocChargeRequested,
    LoanInstallmentListQuery,
    LoanInstallmentListResponse,
    LoanPaymentListQuery,
    LoanPaymentListResponse,
    LoanPortfolioResponse,
    LoanPrepaymentQuote,
    SellerCreditDetailResponse,
    SellerCreditListResponse,
    SellerCreditLookupResult,
    SellerPaymentOptions,
    SellerPaymentResult,
} from './contract';

const BASE_URL = import.meta.env.VITE_API_LOAN_CREDIT_BASE_URL;

/** Toda llamada de este servicio va a SU base path — no al del rbac. */
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL });
}

/** `?a=1&b=2`, o cadena vacía si no hay nada que mandar. */
function queryString(params: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) search.set(key, String(value));
    }
    const rendered = search.toString();
    return rendered ? `?${rendered}` : '';
}

/**
 * `publicGetBorrower` — GET /borrowers/{borrowerId}. Permiso `loan.borrower.read`, scope TENANT.
 * `sciScore` y `clientLevel` llegan ausentes: el único SCI con contrato es `sciAtOrigination`.
 */
export function getBorrower(borrowerId: string): Promise<LoanBorrowerResponse> {
    return call<LoanBorrowerResponse>(`/borrowers/${encodeURIComponent(borrowerId)}`);
}

/**
 * `publicListBorrowerCredits` — GET /borrowers/{borrowerId}/credits. Todos los créditos del
 * acreditado, vigentes y cerrados, cada uno con su avance, su saldo, su mora y la venta que lo
 * originó. Permiso `loan.credit.read`.
 */
export function listBorrowerCredits(borrowerId: string): Promise<LoanBorrowerCreditItem[]> {
    return call<LoanBorrowerCreditItem[]>(`/borrowers/${encodeURIComponent(borrowerId)}/credits`);
}

/**
 * `publicGetCredit` — GET /credits/{creditId}. Estado, checklist de activación, términos, el
 * avance del plan y la venta que originó el crédito. Permiso `loan.credit.read`.
 */
export function getCredit(creditId: string): Promise<LoanCreditDetail> {
    return call<LoanCreditDetail>(`/credits/${encodeURIComponent(creditId)}`);
}

/**
 * `publicListCreditInstallments` — GET /credits/{creditId}/installments. El plan de pagos en orden,
 * con capital/interés/IVA/total por cuota. Permiso `loan.credit.read`.
 */
export function listCreditInstallments(creditId: string): Promise<LoanInstallmentResponse[]> {
    return call<LoanInstallmentResponse[]>(`/credits/${encodeURIComponent(creditId)}/installments`);
}

/**
 * `publicListSellerCredits` — GET /seller/credits. La cartera viva del vendedor del token, completa.
 * Sin `statuses` el lambda lee ACTIVE e IN_ARREARS. Permiso `loan.credit.collection.read.own`.
 */
export function listSellerCredits(
    statuses?: LoanCreditStatusEnum[],
): Promise<SellerCreditListResponse> {
    return call<SellerCreditListResponse>(
        `/seller/credits${queryString({ status: statuses?.length ? statuses.join(',') : undefined })}`,
    );
}

/** `publicGetSellerCredit` — GET /seller/credits/{creditId}. Crédito propio, plan y últimos cobros; ajeno es 404. */
export function getSellerCredit(creditId: string): Promise<SellerCreditDetailResponse> {
    return call<SellerCreditDetailResponse>(`/seller/credits/${encodeURIComponent(creditId)}`);
}

/**
 * `publicListCreditPayments` — GET /credits/{creditId}/payments. UNA página de los pagos asentados,
 * del más reciente al más viejo. Permiso `loan.credit.read`.
 */
export function listCreditPayments(
    creditId: string,
    query: LoanPaymentListQuery = {},
): Promise<LoanPaymentListResponse> {
    return call<LoanPaymentListResponse>(
        `/credits/${encodeURIComponent(creditId)}/payments${queryString({
            limit: query.limit,
            nextToken: query.nextToken,
        })}`,
    );
}

/** `publicCreateSellerPayment` — el vendedor anota un pago recibido en tienda. Permiso `loan.credit.collection.write`. */
export function createSellerPayment(
    creditId: string,
    body: CreateSellerPaymentRequest,
): Promise<SellerPaymentResult> {
    return call<SellerPaymentResult>(`/seller/credits/${encodeURIComponent(creditId)}/payments`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/** `publicGetSellerPaymentOptions` — resumen del crédito y monto o rango de cada tipo de cobro. */
export function getSellerPaymentOptions(creditId: string): Promise<SellerPaymentOptions> {
    return call<SellerPaymentOptions>(
        `/seller/credits/${encodeURIComponent(creditId)}/payment-options`,
    );
}

/** `publicLookupSellerCredit` — walk-in por número de crédito, IMEI o teléfono; otra cadena es 404. */
export function lookupSellerCredit(identifier: string): Promise<SellerCreditLookupResult> {
    return call<SellerCreditLookupResult>(`/seller/credits/lookup${queryString({ identifier })}`);
}

/** `publicListSellerCreditPayments` — historial paginado del crédito para el vendedor. */
export function listSellerCreditPayments(
    creditId: string,
    query: LoanPaymentListQuery = {},
): Promise<LoanPaymentListResponse> {
    return call<LoanPaymentListResponse>(
        `/seller/credits/${encodeURIComponent(creditId)}/payments${queryString({
            limit: query.limit,
            nextToken: query.nextToken,
        })}`,
    );
}

/** `publicListStoreInStorePayments` — conciliación: cobros en tienda de una tienda en un rango. */
export function listStoreInStorePayments(
    storeId: string,
    query: InStorePaymentListQuery,
): Promise<InStorePaymentListResponse> {
    return call<InStorePaymentListResponse>(
        `/seller/stores/${encodeURIComponent(storeId)}/in-store-payments${queryString({
            from: query.from,
            to: query.to,
            limit: query.limit,
            nextToken: query.nextToken,
        })}`,
    );
}

/**
 * `backofficeListCreditNotifications` — GET /backoffice/credits/{creditId}/notifications. UNA página
 * de avisos del crédito, del más nuevo al más viejo. Permiso `loan.credit.read`.
 */
export function listCreditNotifications(
    creditId: string,
    query: LoanCreditNotificationListQuery = {},
): Promise<LoanCreditNotificationListResponse> {
    return call<LoanCreditNotificationListResponse>(
        `/backoffice/credits/${encodeURIComponent(creditId)}/notifications${queryString({
            nextToken: query.nextToken,
        })}`,
    );
}

/**
 * `publicListCreditContracts` — GET /credits/{creditId}/contracts. Metadata de los contratos FES,
 * sin URLs firmadas. Permiso `loan.credit.read`.
 */
export function listCreditContracts(creditId: string): Promise<LoanContractResponse[]> {
    return call<LoanContractResponse[]>(`/credits/${encodeURIComponent(creditId)}/contracts`);
}

/**
 * `publicGetContractDownloadUrl` — GET /credits/{creditId}/contracts/{contractId}/url. La URL
 * prefirmada del PDF, emitida por document-generator. Permiso `loan.credit.read`, scope TENANT.
 * Un contrato sin PDF generado responde 409 `CONTRACT_PDF_NOT_READY`.
 */
export function getContractDownloadUrl(
    creditId: string,
    contractId: string,
): Promise<LoanContractDownloadUrl> {
    return call<LoanContractDownloadUrl>(
        `/credits/${encodeURIComponent(creditId)}/contracts/${encodeURIComponent(contractId)}/url`,
    );
}

/**
 * `publicListBorrowersPortfolio` — GET /borrowers/portfolio. La cartera de un LOTE de clientes
 * retail: traduce cada `retailCustomerId` a su acreditado y agrega SCI, nivel, créditos, saldo y
 * mora. Máximo 100 ids por llamada. Permiso `loan.portfolio.read`, sin scope.
 */
export function listBorrowersPortfolio(
    retailCustomerIds: string[],
): Promise<LoanPortfolioResponse> {
    return call<LoanPortfolioResponse>(
        `/borrowers/portfolio${queryString({ retailCustomerIds: retailCustomerIds.join(',') })}`,
    );
}

/**
 * `backofficeListCredits` — GET /backoffice/credits. UNA página de la cartera del tenant, del más
 * nuevo al más viejo, con el bloqueo del equipo ya resuelto adentro. Permiso `loan.credit.read`,
 * scope TENANT. La siguiente página se pide con el `nextToken` de la anterior.
 */
export function listBackofficeCredits(
    query: LoanCreditPortfolioQuery = {},
): Promise<LoanCreditPortfolioResponse> {
    return call<LoanCreditPortfolioResponse>(
        `/backoffice/credits${queryString({
            status: query.status,
            limit: query.limit,
            nextToken: query.nextToken,
        })}`,
    );
}

/**
 * `backofficeListInstallments` — GET /backoffice/installments: una página de cuotas por `status`,
 * o el plan entero del crédito cuando se busca por `creditId` o `imei`.
 */
export function listBackofficeInstallments(
    query: LoanInstallmentListQuery,
): Promise<LoanInstallmentListResponse> {
    return call<LoanInstallmentListResponse>(
        `/backoffice/installments${queryString({
            status: query.status,
            dueFrom: query.dueFrom,
            dueTo: query.dueTo,
            limit: query.limit,
            nextToken: query.nextToken,
            creditId: query.creditId,
            imei: query.imei,
        })}`,
    );
}

/**
 * `backofficeListCreditCustomers` — GET /backoffice/credit-customers. La ÚNICA llamada que arma la
 * tabla de `/credit/customers`: el BFF cruza padrón, cartera y segmentos. Permiso
 * `loan.customer.list`, scope TENANT. El `nextToken` es opaco y se pasea tal cual.
 */
export function listCreditCustomers(
    query: CreditCustomersQuery = {},
): Promise<CreditCustomersPage> {
    return call<CreditCustomersPage>(
        `/backoffice/credit-customers${queryString({ limit: query.limit, nextToken: query.nextToken })}`,
    );
}

/**
 * `backofficeGetPrepaymentQuote` — GET /backoffice/credits/{creditId}/prepayment-quote. Saldo
 * insoluto, pago del periodo, mínimo del abono según el plan y monto de liquidación. Permiso
 * `loan.credit.prepay`, scope TENANT. Un crédito sin activar responde 409 `CREDIT_NOT_CURRENT`.
 */
export function getPrepaymentQuote(creditId: string): Promise<LoanPrepaymentQuote> {
    return call<LoanPrepaymentQuote>(
        `/backoffice/credits/${encodeURIComponent(creditId)}/prepayment-quote`,
    );
}

/**
 * `backofficeApplyPrepayment` — POST /backoffice/credits/{creditId}/prepayments. Pide al motor el
 * cargo del abono y responde 202; el libro se mueve cuando llega su resultado. Permiso
 * `loan.credit.prepay`. Rechazos: 409 `CREDIT_NOT_CURRENT` · `AD_HOC_CHARGE_PENDING` ·
 * `CREDIT_PLAN_BATCHES_PENDING`; 422 `PREPAYMENT_BELOW_PERIOD_PAYMENT` ·
 * `PREPAYMENT_COVERS_SETTLEMENT_AMOUNT`; 502 `COLLECTION_ENGINE_UNAVAILABLE`.
 */
export function applyPrepayment(
    creditId: string,
    body: BackofficeApplyPrepaymentRequest,
): Promise<LoanAdHocChargeRequested> {
    return call<LoanAdHocChargeRequested>(
        `/backoffice/credits/${encodeURIComponent(creditId)}/prepayments`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

/** Días del contador de cobranza: los mismos que pinta la tira del cajón. */
export const COLLECTION_QUOTA_DAYS = 14;

/**
 * `backofficeGetCustomerCollectionQuota` — GET /backoffice/customers/{id}/collection-quota. Los
 * mensajes de cobranza por día y canal. Permiso `loan.credit.read`.
 */
export function getCustomerCollectionQuota(
    retailCustomerId: string,
    days: number = COLLECTION_QUOTA_DAYS,
): Promise<CollectionQuotaResponse> {
    return call<CollectionQuotaResponse>(
        `/backoffice/customers/${encodeURIComponent(retailCustomerId)}/collection-quota${queryString({ days })}`,
    );
}

/**
 * `backofficeSendCustomerManualMessage` — POST /backoffice/customers/{id}/messages. Encola un
 * momento del crédito con envío manual. Permiso `loan.notice.manual.send`.
 */
export function sendCustomerManualMessage(
    retailCustomerId: string,
    body: CustomerManualMessageRequest,
): Promise<CustomerManualMessageResponse> {
    return call<CustomerManualMessageResponse>(
        `/backoffice/customers/${encodeURIComponent(retailCustomerId)}/messages`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

export const loanCreditApi = {
    applyPrepayment,
    getBorrower,
    getPrepaymentQuote,
    listCreditCustomers,
    getContractDownloadUrl,
    listBackofficeCredits,
    listBackofficeInstallments,
    listBorrowersPortfolio,
    listBorrowerCredits,
    getCredit,
    listCreditInstallments,
    listCreditPayments,
    listCreditNotifications,
    listCreditContracts,
    createSellerPayment,
    getSellerPaymentOptions,
    lookupSellerCredit,
    listSellerCreditPayments,
    listStoreInStorePayments,
};

export type LoanCreditApi = typeof loanCreditApi;

/** `backofficeListMdmDevices` — GET /backoffice/devices: una página de la flota o de un `status`,
 *  cada uno con su cursor; con `imei`, todos los equipos de ese IMEI sin cursor. */
export function listMdmDevices(query: LoanDeviceFleetQuery = {}): Promise<LoanDeviceFleetResponse> {
    return call<LoanDeviceFleetResponse>(
        `/backoffice/devices${queryString({
            status: query.status,
            limit: query.limit,
            nextToken: query.nextToken,
            imei: query.imei,
        })}`,
    );
}

/**
 * `backofficeGetMdmDevice` — GET /backoffice/devices/{creditId}: el expediente MDM de un crédito,
 * sin consultar al proveedor. Sin equipo responde 404 `MDM_DEVICE_NOT_FOUND`.
 */
export function getMdmDevice(creditId: string): Promise<LoanDeviceFleetItem> {
    return call<LoanDeviceFleetItem>(`/backoffice/devices/${encodeURIComponent(creditId)}`);
}

/**
 * `backofficeListMdmDeviceOperations` — GET /backoffice/devices/{creditId}/operations. La bitácora
 * de UN equipo, de la más reciente a la más vieja. Permiso `mdm.operation.log.view`.
 */
export function listMdmDeviceOperations(
    creditId: string,
    limit?: number,
    nextToken?: string,
): Promise<LoanDeviceOperationsPage> {
    return call<LoanDeviceOperationsPage>(
        `/backoffice/devices/${encodeURIComponent(creditId)}/operations${queryString({ limit, nextToken })}`,
    );
}

/**
 * `backofficeLockMdmDevice` — POST /backoffice/devices/{creditId}/lock. Permiso
 * `mdm.device.lock.manual`. Reusa el `manualLock` del lambda con un lote de UN ítem.
 */
export function lockMdmDevice(
    creditId: string,
    body: LoanDeviceLockRequest,
): Promise<LoanDeviceLockResult> {
    return call<LoanDeviceLockResult>(`/backoffice/devices/${encodeURIComponent(creditId)}/lock`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * `backofficeUnlockMdmDevice` — POST /backoffice/devices/{creditId}/unlock, con motivo y nota
 * opcionales. En `mdmLockMode = SCHEDULED` no cancela el autolock programado.
 */
export function unlockMdmDevice(
    creditId: string,
    body: LoanDeviceUnlockRequest = {},
): Promise<LoanDeviceUnlockResult> {
    return call<LoanDeviceUnlockResult>(
        `/backoffice/devices/${encodeURIComponent(creditId)}/unlock`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

/**
 * `backofficeSendRegulatoryNotice` — POST /backoffice/regulatory-notices. Permiso
 * `loan.notice.regulatory.send`. Manda UNA página; se repite mientras traiga `nextToken`.
 */
export function sendRegulatoryNotice(
    body: BackofficeSendRegulatoryNoticeRequest,
): Promise<RegulatoryNoticeCampaignPage> {
    return call<RegulatoryNoticeCampaignPage>('/backoffice/regulatory-notices', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * `backofficePinUnlockMdmDevice` — POST /backoffice/devices/{creditId}/pin-unlock. Permiso
 * `mdm.device.pin.unlock`. NO cambia el estado del equipo: pide el passcode al proveedor para que
 * el operador se lo dicte al cliente sin señal. El `pin` es de un solo uso y no se persiste.
 */
export function pinUnlockMdmDevice(
    creditId: string,
    body: { devicePasskey: string },
): Promise<LoanDevicePinUnlockResult> {
    return call<LoanDevicePinUnlockResult>(
        `/backoffice/devices/${encodeURIComponent(creditId)}/pin-unlock`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

/**
 * `backofficeExtendMdmDeviceValidity` — POST /backoffice/devices/{creditId}/extend-validity.
 * Permiso `mdm.device.validity.extend`. Son DOS llamadas al proveedor: desbloquear y reprogramar
 * el corte. Responde 200 con la fase alcanzada; el rechazo viaja en `error`, no en el status HTTP.
 */
export function extendMdmDeviceValidity(
    creditId: string,
    body: LoanDeviceExtendValidityRequest,
): Promise<LoanDeviceExtendValidityResult> {
    return call<LoanDeviceExtendValidityResult>(
        `/backoffice/devices/${encodeURIComponent(creditId)}/extend-validity`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

/**
 * `backofficeNotifyMdmDevice` — POST /backoffice/devices/{creditId}/notify. Permiso
 * `mdm.device.notify`. NO cambia el estado del expediente: notificar no bloquea ni desbloquea.
 */
export function notifyMdmDevice(
    creditId: string,
    body: LoanDeviceNotifyRequest,
): Promise<LoanDeviceNotifyResult> {
    return call<LoanDeviceNotifyResult>(
        `/backoffice/devices/${encodeURIComponent(creditId)}/notify`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

/**
 * `backofficeReleaseMdmDevice` — POST /backoffice/devices/{creditId}/release. Permiso
 * `mdm.device.release`. IRREVERSIBLE: el expediente queda RELEASED y un segundo release vuelve
 * con 200 y `error.code = CONFLICT`.
 */
export function releaseMdmDevice(
    creditId: string,
    body: LoanDeviceReleaseRequest,
): Promise<LoanDeviceReleaseResult> {
    return call<LoanDeviceReleaseResult>(
        `/backoffice/devices/${encodeURIComponent(creditId)}/release`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

/**
 * `backofficeRetryMdmDeviceEnroll` — POST /backoffice/devices/{creditId}/retry-enroll. Sin body.
 * Permiso `mdm.device.enroll.retry`. Solo procede desde FAILED o MANUAL_REVIEW; cualquier otro
 * estado vuelve con 200 y `error.code`.
 */
export function retryMdmDeviceEnroll(creditId: string): Promise<LoanDeviceEnrollResult> {
    return call<LoanDeviceEnrollResult>(
        `/backoffice/devices/${encodeURIComponent(creditId)}/retry-enroll`,
        { method: 'POST', body: JSON.stringify({}) },
    );
}

/**
 * `backofficeRefreshMdmDevice` — POST /backoffice/devices/{creditId}/refresh. Reconsulta al proveedor,
 * persiste lo observado y devuelve la fila YA actualizada junto al veredicto de la consulta.
 * Permiso `mdm.device.status.refresh`.
 */
export function refreshMdmDevice(creditId: string): Promise<LoanDeviceRefreshResult> {
    return call<LoanDeviceRefreshResult>(
        `/backoffice/devices/${encodeURIComponent(creditId)}/refresh`,
        { method: 'POST', body: JSON.stringify({}) },
    );
}

export const mdmBackofficeApi = {
    getMdmDevice,
    listMdmDevices,
    listMdmDeviceOperations,
    lockMdmDevice,
    pinUnlockMdmDevice,
    unlockMdmDevice,
    extendMdmDeviceValidity,
    retryMdmDeviceEnroll,
    refreshMdmDevice,
    notifyMdmDevice,
    releaseMdmDevice,
};

export type MdmBackofficeApi = typeof mdmBackofficeApi;
